import React, { useState, useRef, useMemo, useEffect, useCallback, useContext, useLayoutEffect, createContext } from 'react'
import { createPortal } from 'react-dom'
import { Box } from '@mui/material'
import { ReactComponent as CalendarIcon } from '../../assets/appointment-icons/calendar.svg'
import { ReactComponent as ClockIcon } from '../../assets/appointment-icons/clock.svg'
import { ReactComponent as UserIcon } from '../../assets/appointment-icons/user.svg'
import { ReactComponent as PawIcon } from '../../assets/appointment-icons/paw.svg'
import { ReactComponent as RepeatIcon } from '../../assets/appointment-icons/repeat.svg'
import { ReactComponent as WarnIcon } from '../../assets/appointment-icons/warn.svg'
import { ReactComponent as CheckcIcon } from '../../assets/appointment-icons/checkc.svg'
import { ReactComponent as PencilIcon } from '../../assets/appointment-icons/pencil.svg'
import { ReactComponent as ChevronIcon } from '../../assets/appointment-icons/chevron.svg'
import { ReactComponent as TickIcon } from '../../assets/appointment-icons/tick.svg'
import { ReactComponent as XIcon } from '../../assets/appointment-icons/x.svg'
import { ReactComponent as PlusIcon } from '../../assets/appointment-icons/plus.svg'

/**
 * LavDropdown — the lavender-themed single-select dropdown (ck-dd) from the
 * Claude Design system, extracted here so any screen (client/pet forms,
 * appointment flow, …) can share one implementation. Also exports the shared
 * design tokens (L), the Icon set, the PortalMenu, and the menu/item sx used by
 * sibling lavender controls.
 */

// Touch-size context — provided (value=true) by mobile shells (e.g. the
// AddAppointmentDialog bottom drawer) so every lavender control sizes itself
// up for touch, mirroring the design's `.mbap` overrides. Defaults to false,
// so desktop usage everywhere else is unchanged.
export const LavMobileContext = createContext(false)
export const useLavMobile = () => useContext(LavMobileContext)

// Lavender design tokens
export const L = {
	bg: '#FDFCFF',
	surface: '#F5F3FB',
	border: '#E7E2F3',
	text: '#2E2A40',
	muted: '#8B85A4',
	primary: '#7C6BD9',
	primaryDeep: '#5F4EC2',
	primarySoft: '#EDE9FB',
	onPrimary: '#FFFFFF',
	field: '#FFFFFF',
	r: '14px',
	rSm: '10px',
	font: "'Outfit', sans-serif",
}

// ---- icon set (SVG assets from src/assets/appointment-icons, exact design glyphs) ----
const ICONS = {
	calendar: CalendarIcon,
	clock: ClockIcon,
	user: UserIcon,
	paw: PawIcon,
	repeat: RepeatIcon,
	warn: WarnIcon,
	checkc: CheckcIcon,
	pencil: PencilIcon,
	chevron: ChevronIcon,
	tick: TickIcon,
	x: XIcon,
	plus: PlusIcon,
}

export function Icon({ name, size = 15, sw }) {
	const Cmp = ICONS[name]
	if (!Cmp) return null
	// Stroke weight is baked into the asset (1.8); only override when asked.
	// `paw` is a fill glyph, so strokeWidth is irrelevant there.
	return (
		<Cmp
			width={size}
			height={size}
			{...(sw != null && name !== 'paw' ? { strokeWidth: sw } : {})}
			style={{ flex: 'none', display: 'block' }}
			aria-hidden="true"
		/>
	)
}

// ---- portal-rendered menu ----
// Renders the dropdown list into document.body with fixed positioning anchored
// to the trigger, so a long list is never clipped by (or forces a scroll of)
// the surrounding scroll container. Flips above the trigger when there isn't
// enough room below. Repositions on scroll/resize while open.
// `footer` renders below the scrolling list, inside the menu's frame but
// outside its scroll area — for rows that must stay reachable however long
// the list is (the dropdown's "Create new…" options).
export function PortalMenu({ anchorRef, open, onClose, children, footer = null }) {
	const mobile = useLavMobile()
	const [pos, setPos] = useState(null)

	const reposition = useCallback(() => {
		const el = anchorRef.current
		if (!el) return

		// The app applies `html { zoom: 0.9 }` on desktop. getBoundingClientRect()
		// returns post-zoom (visual) coordinates, but the menu is portaled into
		// <body> — still inside the zoomed <html> — so any left/top/width we set
		// gets multiplied by the zoom again. Convert the rect (and the viewport)
		// into the menu's own pre-zoom coordinate space by dividing by the zoom
		// factor so the menu lines up with the trigger at any zoom level.
		const zoom = parseFloat(getComputedStyle(document.documentElement).zoom) || 1
		const br = el.getBoundingClientRect()
		const r = {
			left: br.left / zoom,
			right: br.right / zoom,
			top: br.top / zoom,
			bottom: br.bottom / zoom,
			width: br.width / zoom,
		}
		const gap = 6 // space between trigger and menu
		const margin = 8 // min distance from any viewport edge
		const maxH = 320
		const vw = window.innerWidth / zoom
		const vh = window.innerHeight / zoom

		// Vertical: open below by default, flip above when there's more room there.
		const spaceBelow = vh - r.bottom - gap - margin
		const spaceAbove = r.top - gap - margin
		const flipUp = spaceBelow < Math.min(maxH, 200) && spaceAbove > spaceBelow
		const maxHeight = Math.max(140, Math.min(maxH, flipUp ? spaceAbove : spaceBelow))

		// Horizontal: align the menu's left edge to the trigger and match its
		// width, but clamp so the menu never spills past either viewport edge.
		const width = Math.min(r.width, vw - margin * 2)
		let left = r.left
		if (left + width > vw - margin) left = vw - margin - width
		if (left < margin) left = margin

		setPos({
			position: 'fixed',
			left,
			right: 'auto',
			width,
			maxHeight,
			...(flipUp
				? { top: 'auto', bottom: vh - r.top + gap }
				: { top: r.bottom + gap, bottom: 'auto' }),
		})
	}, [anchorRef])

	useLayoutEffect(() => {
		if (!open) return
		reposition()
		// Capture phase so we also catch scrolls on inner scroll containers.
		window.addEventListener('scroll', reposition, true)
		window.addEventListener('resize', reposition)
		return () => {
			window.removeEventListener('scroll', reposition, true)
			window.removeEventListener('resize', reposition)
		}
	}, [open, reposition])

	if (!open || !pos) return null

	return createPortal(
		<>
			<Box onClick={onClose} sx={{ position: 'fixed', inset: 0, zIndex: 1400 }} />
			<Box role="listbox" sx={{
				...menuSx,
				// The frame clips; the inner box scrolls, so a footer can sit below
				// the list without the list showing through under it.
				p: 0, overflow: 'hidden',
				...(mobile ? { borderRadius: '14px' } : {}),
				...pos,
				zIndex: 1401,
			}}>
				<Box sx={{
					flex: 1, minHeight: 0, overflowY: 'auto',
					scrollbarWidth: 'thin', scrollbarColor: `${L.border} transparent`,
					display: 'flex', flexDirection: 'column', gap: '2px', p: mobile ? '7px' : '6px',
				}}>
					{children}
				</Box>
				{footer ? (
					<Box sx={{
						flex: 'none', position: 'relative', display: 'flex', flexDirection: 'column', gap: '2px',
						p: mobile ? '5px 7px 7px' : '4px 6px 6px', bgcolor: '#FFFFFF',
						borderTop: `1px solid ${L.border}`, boxShadow: '0 -8px 14px -10px rgba(46, 42, 64, 0.22)',
					}}>
						{footer}
					</Box>
				) : null}
			</Box>
		</>,
		document.body,
	)
}

// ---- generic single-select dropdown (ck-dd) ----
// options: [{ id, label, sub?, meta?, isCreateOption? }]
// `searchable` turns the control into a combobox — the field itself is typed
// into to filter the list. Use it for long lists (breeds, services…) so the
// value can be typed instead of scrolled.
export function LavDropdown({
	icon, placeholder, value, options, onSelect,
	disabled = false, clearable = false, defaultOpen = false,
	searchable = false,
}) {
	const mobile = useLavMobile()
	const [open, setOpen] = useState(Boolean(defaultOpen))
	const [query, setQuery] = useState('')
	// The query is seeded with the selected label on open so it can be edited
	// or deleted; until the user actually types, the full list is shown.
	const [edited, setEdited] = useState(false)
	const [activeIndex, setActiveIndex] = useState(0)
	const triggerRef = useRef(null)
	const searchRef = useRef(null)
	const activeRef = useRef(null)
	const sel = options.find((o) => o.id === value) || null

	const q = searchable && edited ? query.trim().toLowerCase() : ''
	// Match on the label or the secondary line (e.g. a client's email/phone).
	// "Create new" options stay visible regardless of the query so a merchant
	// who searched for a client that doesn't exist can add them right there.
	const shown = useMemo(
		() => (q
			? options.filter((o) => o.isCreateOption
				|| String(o.label ?? '').toLowerCase().includes(q)
				|| String(o.sub ?? '').toLowerCase().includes(q))
			: options),
		[options, q],
	)

	// The scrolling list vs the pinned footer ("Create new…" rows).
	const listOptions = shown.filter((o) => !o.isCreateOption)
	const createOptions = shown.filter((o) => o.isCreateOption)

	const close = useCallback(() => {
		setOpen(false)
		setQuery('')
		setEdited(false)
		setActiveIndex(0)
	}, [])

	const choose = useCallback((id) => {
		onSelect(id)
		close()
	}, [onSelect, close])

	// Read the latest options/value inside the open effect without making them
	// dependencies — `options` is usually a fresh array each parent render, and
	// re-running would reset the keyboard highlight and re-steal focus mid-typing.
	const latest = useRef({ options, value })
	latest.current = { options, value }

	// On open, focus the filter and highlight the current value.
	useEffect(() => {
		if (!open || !searchable) return
		const { options: opts, value: val } = latest.current
		const cur = opts.find((o) => o.id === val)
		setActiveIndex(Math.max(0, opts.findIndex((o) => o.id === val)))
		setQuery(cur ? String(cur.label ?? '') : '')
		setEdited(false)
		// The menu portals in after PortalMenu measures the trigger, so defer.
		// Put the caret at the end of the seeded label so it can be backspaced.
		const t = setTimeout(() => {
			const el = searchRef.current
			if (!el) return
			el.focus()
			const len = el.value.length
			try { el.setSelectionRange(len, len) } catch (_) { /* unsupported input type */ }
		}, 0)
		return () => clearTimeout(t)
	}, [open, searchable])

	// Keep the keyboard-highlighted option in view.
	useEffect(() => {
		if (open) activeRef.current?.scrollIntoView({ block: 'nearest' })
	}, [open, activeIndex])

	const onSearchKeyDown = (e) => {
		if (e.key === 'ArrowDown') {
			e.preventDefault()
			if (!open) { setOpen(true); return }
			setActiveIndex((i) => Math.min(i + 1, shown.length - 1))
		} else if (e.key === 'ArrowUp') {
			e.preventDefault()
			setActiveIndex((i) => Math.max(i - 1, 0))
		} else if (e.key === 'Enter') {
			e.preventDefault()
			const o = shown[activeIndex]
			if (o) choose(o.id)
		} else if (e.key === 'Escape') {
			e.preventDefault()
			close()
		}
	}

	const triggerBoxSx = {
		display: 'flex', alignItems: 'center', gap: '9px', width: '100%',
		height: mobile ? '50px' : '42px', px: mobile ? '13px' : '12px',
		border: `1.5px solid ${open ? L.primary : L.border}`,
		borderRadius: mobile ? '12px' : L.rSm,
		bgcolor: L.field, fontSize: mobile ? '15px' : '14px', fontWeight: 600, color: L.text, fontFamily: L.font,
		opacity: disabled ? 0.55 : 1,
		boxShadow: open ? `0 0 0 3px ${L.primarySoft}` : 'none',
		transition: 'border-color .15s, box-shadow .15s',
		'&:hover': { borderColor: disabled ? L.border : L.primary },
		'& > svg': { color: L.muted },
	}

	// meta / clear / chevron — identical for both trigger styles
	const trailing = (
		<>
			{sel && sel.meta ? (
				<Box component="span" sx={{
					fontSize: mobile ? '13px' : '12px', color: L.muted, fontVariantNumeric: 'tabular-nums',
					flex: 'none', fontWeight: 600,
				}}>
					{sel.meta}
				</Box>
			) : null}
			{sel && clearable ? (
				<Box
					component="span"
					role="button"
					aria-label="Clear selection"
					onClick={(e) => { e.stopPropagation(); onSelect(null); close() }}
					sx={{
						display: 'flex', alignItems: 'center', justifyContent: 'center',
						width: '22px', height: '22px', flex: 'none', borderRadius: '50%', color: L.muted,
						cursor: 'pointer',
						'&:hover': { bgcolor: L.surface, color: L.text },
					}}
				>
					<Icon name="x" size={13} />
				</Box>
			) : null}
			<Box component="span" sx={{
				display: 'flex', color: L.muted, transition: 'transform .15s', flex: 'none',
				transform: open ? 'rotate(180deg)' : 'none',
			}}>
				<Icon name="chevron" size={14} />
			</Box>
		</>
	)

	return (
		<Box sx={{ position: 'relative', fontFamily: L.font }}>
			{searchable ? (
				// Combobox: the field itself is typed into. Closed it shows the
				// selected label; open it clears to a query and keeps the selection
				// visible as the placeholder.
				<Box
					ref={triggerRef}
					onClick={() => { if (!disabled) { setOpen(true); searchRef.current?.focus() } }}
					sx={{ ...triggerBoxSx, cursor: disabled ? 'default' : 'text' }}
				>
					{icon ? <Box component="span" sx={{ display: 'flex', color: L.muted, flex: 'none' }}>{icon}</Box> : null}
					<Box
						component="input"
						ref={searchRef}
						type="text"
						role="combobox"
						aria-expanded={open}
						aria-autocomplete="list"
						autoComplete="off"
						disabled={disabled}
						placeholder={placeholder}
						value={open ? query : (sel ? sel.label : '')}
						onFocus={() => { if (!disabled) setOpen(true) }}
						onChange={(e) => { setQuery(e.target.value); setEdited(true); setActiveIndex(0); if (!open) setOpen(true) }}
						onKeyDown={onSearchKeyDown}
						sx={{
							flex: 1, minWidth: 0, p: 0, border: 'none', outline: 'none', bgcolor: 'transparent',
							fontFamily: L.font, fontSize: mobile ? '15px' : '14px', fontWeight: 600,
							color: L.text, textOverflow: 'ellipsis',
							cursor: disabled ? 'default' : 'text',
							'&::placeholder': { color: L.muted, fontWeight: 500, opacity: 1 },
						}}
					/>
					{trailing}
				</Box>
			) : (
				<Box
					ref={triggerRef}
					component="button"
					type="button"
					disabled={disabled}
					aria-expanded={open}
					onClick={() => { if (disabled) return; if (open) close(); else setOpen(true) }}
					sx={{ ...triggerBoxSx, cursor: disabled ? 'default' : 'pointer' }}
				>
					{icon ? <Box component="span" sx={{ display: 'flex', color: L.muted, flex: 'none' }}>{icon}</Box> : null}
					<Box component="span" sx={{
						flex: 1, textAlign: 'left', minWidth: 0, overflow: 'hidden',
						textOverflow: 'ellipsis', whiteSpace: 'nowrap',
						color: sel ? L.text : L.muted, fontWeight: sel ? 600 : 500,
					}}>
						{sel ? sel.label : placeholder}
					</Box>
					{trailing}
				</Box>
			)}

			<PortalMenu
				anchorRef={triggerRef}
				open={open}
				onClose={close}
				// "Create new…" lives in the menu's footer, below the scroll area,
				// so it's reachable however long the list is. It stays last in
				// `shown`, so keyboard order is unchanged.
				footer={createOptions.length ? createOptions.map((o) => renderOption(o, shown.indexOf(o))) : null}
			>
				{listOptions.length === 0 && (
					<Box sx={{ p: '20px 10px', textAlign: 'center', color: L.muted, fontSize: '12.5px' }}>
						{q ? `No matches for “${query.trim()}”` : 'No options'}
					</Box>
				)}
				{listOptions.map((o) => renderOption(o, shown.indexOf(o)))}
			</PortalMenu>
		</Box>
	)

	// Hoisted so both the list and the footer render options the same way.
	// `i` is the option's index in `shown` (what activeIndex refers to).
	function renderOption(o, i) {
					const on = o.id === value
					const active = searchable && i === activeIndex
					return (
						<Box
							component="button"
							type="button"
							key={o.id}
							role="option"
							aria-selected={on}
							ref={active ? activeRef : null}
							onMouseEnter={searchable ? () => setActiveIndex(i) : undefined}
							onClick={() => choose(o.id)}
							sx={{
								...itemSx,
								...(mobile ? { minHeight: '48px', borderRadius: '11px', fontSize: '14.5px', p: '9px 12px' } : {}),
								...(active && !on ? { bgcolor: L.surface } : {}),
								...(on ? { bgcolor: L.primarySoft, color: L.primaryDeep } : {}),
								...(o.isCreateOption ? { color: L.primaryDeep, fontWeight: 700 } : {}),
							}}
						>
							<Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, gap: '1px', flex: 1 }}>
								<Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
									{o.label}
								</Box>
								{o.sub ? (
									<Box component="small" sx={{ fontSize: mobile ? '12px' : '11px', color: L.muted, fontWeight: 600 }}>
										{o.sub}
									</Box>
								) : null}
							</Box>
							{o.meta ? (
								<Box component="span" sx={{
									fontSize: mobile ? '13px' : '12px', color: on ? L.primary : L.muted,
									fontVariantNumeric: 'tabular-nums', flex: 'none', fontWeight: 600,
								}}>
									{o.meta}
								</Box>
							) : null}
							{on ? (
								<Box component="span" sx={{ display: 'flex', ml: '6px', color: L.primaryDeep, flex: 'none' }}>
									<Icon name="tick" size={14} sw={2.2} />
								</Box>
							) : null}
						</Box>
					)
	}
}

export const menuSx = {
	bgcolor: '#FFFFFF', border: `1px solid ${L.border}`, borderRadius: '13px', p: '6px',
	display: 'flex', flexDirection: 'column', gap: '2px', overflowY: 'auto',
	scrollbarWidth: 'thin', scrollbarColor: `${L.border} transparent`,
	boxShadow: '0 14px 36px rgba(95, 78, 194, 0.16), 0 3px 10px rgba(95, 78, 194, 0.08)',
}

export const itemSx = {
	display: 'flex', alignItems: 'center', gap: '9px', flex: 'none', p: '8px 10px',
	border: 'none', borderRadius: '9px', bgcolor: 'transparent', fontFamily: L.font,
	fontSize: '13.5px', fontWeight: 600, color: L.text, textAlign: 'left', width: '100%', cursor: 'pointer',
	'&:hover': { bgcolor: L.surface },
}
